import { useClientSearch } from '@/hooks/useClientSearch';
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ClientSearchResult } from '../../services/clientService';
import { ClientSuggestionItem } from './ClientSuggestionItem';

interface SmartClientSearchInputProps {
  placeholder?: string;
  onClientSelect: (client: ClientSearchResult) => void;
  onCustomInput: (query: string) => void;
  onEnterPress?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  className?: string;
  inputClassName?: string;
  value?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

export interface SmartClientSearchInputRef {
  focus: () => void;
  focusAndSelect: () => void;
}

export const SmartClientSearchInput = forwardRef<
  SmartClientSearchInputRef,
  SmartClientSearchInputProps
>(
  (
    {
      placeholder = 'Rechercher un client ou saisir un nom...',
      onClientSelect,
      onCustomInput,
      onEnterPress,
      onBlur,
      onClick,
      className = '',
      inputClassName = '',
      value = '',
      readOnly = false,
      disabled = false,
    },
    ref
  ) => {
    const [query, setQuery] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [hasUserTyped, setHasUserTyped] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Exposer les méthodes focus et focusAndSelect au parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      focusAndSelect: () => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      },
    }));

    // Utilisation du hook optimisé
    const { suggestions, isLoading, error, searchClients, clearSuggestions } = useClientSearch({
      limit: 5,
      debounceMs: 300,
    });

    // Gestion du changement de texte
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return; // Ne pas permettre la modification en lecture seule

      const inputValue = e.target.value;
      setQuery(inputValue);
      setHasUserTyped(true);

      if (inputValue.trim().length >= 1) {
        searchClients(inputValue);
        setShowSuggestions(true);
      } else {
        clearSuggestions();
        setShowSuggestions(false);
      }

      setSelectedIndex(-1);

      // Notifier le parent du changement pour client personnalisé
      onCustomInput(inputValue);
    };

    // Gestion de la navigation au clavier
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || suggestions.length === 0) {
        // Si pas de suggestions et que l'utilisateur appuie sur Entrée
        if (e.key === 'Enter' && query.trim()) {
          e.preventDefault();
          onEnterPress?.();
          return;
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev === -1 ? 0 : Math.min(prev + 1, suggestions.length - 1)));
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev <= 0 ? -1 : prev - 1));
          break;

        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleClientSelect(suggestions[selectedIndex]);
          } else if (query.trim()) {
            // Si pas de sélection mais qu'il y a du texte, on passe au suivant
            onEnterPress?.();
          }
          break;

        case 'Escape':
          setShowSuggestions(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
          break;
      }
    };

    // Gestion de la sélection d'un client
    const handleClientSelect = (client: ClientSearchResult) => {
      setQuery(client.full_name);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setHasUserTyped(false);
      onClientSelect(client);
    };

    // Fermer les suggestions quand on clique ailleurs
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target as Node) &&
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Mettre à jour l'affichage des suggestions quand les résultats changent
    useEffect(() => {
      if (query.trim().length >= 1 && hasUserTyped) {
        // Toujours afficher la dropdown si on a tapé au moins 1 caractère
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }, [suggestions, query, isLoading, error, hasUserTyped]);

    // Synchroniser avec la valeur externe
    useEffect(() => {
      if (value !== query) {
        setQuery(value);
      }
    }, [value]);

    return (
      <div className={`relative ${className}`}>
        {/* Input de recherche */}
        <div className="relative">
          {/* Wrapper cliquable pour gérer le clic sur input disabled */}
          {disabled && (
            <div
              className="absolute inset-0 z-10 cursor-pointer"
              onClick={onClick}
              title="Cliquez pour réinitialiser"
            />
          )}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0 && hasUserTyped) {
                setShowSuggestions(true);
              }
            }}
            onBlur={onBlur}
            placeholder={placeholder}
            className={`w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-sm focus:outline-none focus:ring-0 focus:border-gray-200 focus:bg-white transition-all duration-200 ${disabled ? 'bg-gray-100 cursor-pointer' : ''} ${inputClassName}`}
            readOnly={readOnly}
            disabled={disabled}
          />
          {isLoading && query.trim().length >= 1 && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
            </div>
          )}
        </div>

        {/* Liste des suggestions */}
        {showSuggestions &&
          !readOnly && // On va rendre l'affichage du conteneur conditionnel
          (error || isLoading || suggestions.length > 0) && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 w-full mt-1 bg-yellow-50 border border-gray-200 rounded-sm shadow-lg overflow-hidden"
            >
              {error && (
                <div className="p-3 text-red-600 text-sm flex items-center space-x-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {!error &&
                suggestions.length > 0 &&
                suggestions.map((client, index) => (
                  <ClientSuggestionItem
                    key={client.id}
                    client={client}
                    isSelected={index === selectedIndex}
                    onClick={() => handleClientSelect(client)}
                  />
                ))}
            </div>
          )}
      </div>
    );
  }
);

SmartClientSearchInput.displayName = 'SmartClientSearchInput';
